import React, { FC, useState } from 'react'
import moment from 'moment/moment'
import { Loader2Icon } from 'lucide-react'
import * as XLSX from 'xlsx-js-style'

import RegistrationDyDayService from '../../../../services/firebase-service/registration-by-day/RegistrationDyDayService'
import RegistrationFeesService from '../../../../services/firebase-service/registration-fees/RegistrationFeesService'
import RegistrationTicketService from '../../../../services/firebase-service/registration-tickets/RegistrationTicketService'

import useToasterHelper from '../../../../helpers/ToasterHelper'
import { addSalesTax, getCurrency } from '../../../../helpers/payment'
import {
  customRows,
  generateCategoriesFinanceReport,
  generateTransactionsFinanceReport,
  getFeesCustomFinanceData,
  getFeesFinanceData,
  getTotalFinanceData,
  IFinanceTotal,
  TransactionRow,
  ICategoryRow,
  IPaymentMethodRow,
  generatePaymentMethodsFinanceReport,
} from '../utils/generateCategoriesFinanceReport'

import { IEventInterface } from '../../../../models/events/event.interface'
import { IEventPaymentSettingsInterface } from '../../../../models/event-payment-settings/event-payment-settings.interface'

import { FEES_CATEGORY_CONST } from '../../../../components/events/views/details/EventDetailsViewComponentFees'
import { PAYMENT_TYPE } from '../../../../types/payment'
import { MESSAGES_CONST } from '../../../../const/messages-const'

interface ExportButtonProps {
  event: IEventInterface | null
  eventPaymentSettings: IEventPaymentSettingsInterface | null
}

export const ExportButton: FC<ExportButtonProps> = ({ event, eventPaymentSettings }) => {
  const toast = useToasterHelper()

  const [loading, setLoading] = useState(false)

  const openExport = async () => {
    try {
      if (!event?.id) return
      setLoading(true)

      const registrationsByDay = await RegistrationDyDayService.getByEventIds([event.id])
      const registrationsFees = await RegistrationFeesService.getByEventId(event.id)
      const registrationsTickets = await RegistrationTicketService.getByEventId(event.id)

      const registrationsByDayUpdated = registrationsByDay.map((item) => ({
        price: (item.registrationPrice || 0) + Number(item.isQualify ? item.qualifyFee || 0 : 0),
        paymentStatus: item.paymentStatus,
        paymentType: item.paymentType,
        isScratched: item.isScratched || false,
        refundAmountAdded: item.refundAmountAdded || false,
        amountRefunded: item.amountRefunded || 0,
        includeSalesTax: item.includeSalesTax || false,
        created: item.created,
      }))

      const registrationsFeesUpdated = registrationsFees.map((item) => ({
        price: item.feesPrice,
        paymentStatus: item.paymentStatus,
        paymentType: item.paymentType,
        isScratched: item.isScratched || false,
        refundAmountAdded: item.refundAmountAdded || false,
        amountRefunded: item.amountRefunded || 0,
        includeSalesTax: item.includeSalesTax || false,
        created: item.created,
      }))

      const registrationsTicketsUpdated = registrationsTickets.map((item) => ({
        price: item.ticketPrice,
        paymentStatus: item.paymentStatus,
        paymentType: item.paymentType,
        isScratched: item.isScratched || false,
        refundAmountAdded: item.refundAmountAdded || false,
        amountRefunded: item.amountRefunded || 0,
        includeSalesTax: item.includeSalesTax || false,
        created: item.created,
      }))

      const allItems: IFinanceTotal[] = [
        ...registrationsByDayUpdated,
        ...registrationsFeesUpdated,
        ...registrationsTicketsUpdated,
      ]

      // Total
      const totalCard = getTotalFinanceData({
        items: allItems,
        paymentType: PAYMENT_TYPE.CARD,
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const totalCash = getTotalFinanceData({
        items: allItems,
        paymentType: PAYMENT_TYPE.CASH,
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const totalCheck = getTotalFinanceData({
        items: allItems,
        paymentType: PAYMENT_TYPE.CHECK,
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const totalPayPal = getTotalFinanceData({
        items: allItems,
        paymentType: PAYMENT_TYPE.PAYPAL,
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const totalVenmo = getTotalFinanceData({
        items: allItems,
        paymentType: PAYMENT_TYPE.VENMO,
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      // Entries
      const entries = getTotalFinanceData({
        items: registrationsByDayUpdated,
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      // Stalls
      const stallsTemporary = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.STALLS,
        title: 'Temporary stalls',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax || false,
      })

      const stallsPermanent = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.STALLS,
        title: 'Permanent stalls',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax || false,
      })

      const stallsPrivate = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.STALLS,
        title: 'Private stalls',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const stallsCircuit = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.STALLS,
        title: 'Circuit stalls',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const stallsMuckFee = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.STALLS,
        title: 'Muck Fee',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const stallsCustom = getFeesCustomFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.STALLS,
        excludeTitles: [
          'Temporary stalls',
          'Permanent stalls',
          'Private stalls',
          'Circuit stalls',
          'Muck Fee',
        ],
      })

      // Bedding
      const beddingShavings = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.BEDDING,
        title: 'Shaving',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const beddingStraw = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.BEDDING,
        title: 'Straw',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const beddingCustom = getFeesCustomFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.BEDDING,
        excludeTitles: ['Shaving', 'Straw'],
      })

      // Feed
      const feedCoastalHay = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.FEED,
        title: 'Coastal hay',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const feedTimothyHay = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.FEED,
        title: 'Timothy hay',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const feedAlfalfaHay = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.FEED,
        title: 'Alfalfa hay',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const feedTnAHay = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.FEED,
        title: 'TnA hay',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const feedCustom = getFeesCustomFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.FEED,
        excludeTitles: ['Coastal hay', 'Timothy hay', 'Alfalfa hay', 'TnA hay'],
      })

      // Other
      const otherPaddock = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.OTHER,
        title: 'Paddock',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const otherRVHookup = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.OTHER,
        title: 'RV hookup',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const otherCustom = getFeesCustomFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.OTHER,
        excludeTitles: ['Paddock', 'RV hookup'],
      })

      // Mandatory
      const mandatoryOfficeFee = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.GOVERNANCE,
        title: 'Office Fee',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const mandatoryShipInGroundFee = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.GOVERNANCE,
        title: 'Ship in / Ground fee',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const mandatoryAmbulanceFee = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.GOVERNANCE,
        title: 'Ambulance fee',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const mandatoryNightWatch = getFeesFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.GOVERNANCE,
        title: 'Night Watch',
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const mandatoryCustom = getFeesCustomFinanceData({
        registrationsFees,
        category: FEES_CATEGORY_CONST.GOVERNANCE,
        excludeTitles: ['Office Fee', 'Ship in / Ground fee', 'Ambulance fee', 'Night Watch'],
      })

      // Tickets
      const tickets = getTotalFinanceData({
        items: registrationsTicketsUpdated,
        chargeSalesTax: eventPaymentSettings?.chargeSalesTax,
      })

      const PAYMENT_METHOD_ROWS: IPaymentMethodRow[] = [
        // Total
        {
          account: 'P-101',
          paid: totalCard.paidAmount,
          sold: totalCard.soldAmount,
          refunded: totalCard.refundedAmount,
          banked: totalCard.bankedAmount,
          description: 'Card & ACH',
          transactions: totalCard.paidItems.length,
        },
        {
          account: 'P-101',
          paid: totalCash.paidAmount,
          sold: totalCash.soldAmount,
          refunded: totalCash.refundedAmount,
          banked: totalCash.bankedAmount,
          description: 'Cash',
          transactions: totalCash.paidItems.length,
        },
        {
          account: 'P-101',
          paid: totalCheck.paidAmount,
          sold: totalCheck.soldAmount,
          refunded: totalCheck.refundedAmount,
          banked: totalCheck.bankedAmount,
          description: 'Check',
          transactions: totalCheck.paidItems.length,
        },
        {
          account: 'P-101',
          paid: totalPayPal.paidAmount,
          sold: totalPayPal.soldAmount,
          refunded: totalPayPal.refundedAmount,
          banked: totalPayPal.bankedAmount,
          description: 'PayPal',
          transactions: totalPayPal.paidItems.length,
        },
        {
          account: 'P-101',
          paid: totalVenmo.paidAmount,
          sold: totalVenmo.soldAmount,
          refunded: totalVenmo.refundedAmount,
          banked: totalVenmo.bankedAmount,
          description: 'Venmo',
          transactions: totalVenmo.paidItems.length,
        },
      ]

      const CATEGORIES_ROWS: ICategoryRow[] = [
        // Entries
        {
          account: 'P-201',
          paid: entries.paidAmount,
          sold: entries.soldAmount,
          refunded: entries.refundedAmount,
          scratched: entries.scratchedAmount,
          banked: entries.bankedAmount,
          pending: entries.pendingAmount,
          forecast: entries.forecastAmount,
          description: 'Entries',
          transactions: entries.paidItems.length,
        },
        // Stalls
        {
          account: 'P-202a',
          paid: stallsTemporary.paidAmount,
          sold: stallsTemporary.soldAmount,
          refunded: stallsTemporary.refundedAmount,
          scratched: stallsTemporary.scratchedAmount,
          banked: stallsTemporary.bankedAmount,
          pending: stallsTemporary.pendingAmount,
          forecast: stallsTemporary.forecastAmount,
          description: 'Stalls - Temporary',
          transactions: stallsTemporary.paidItems.length,
        },
        {
          account: 'P-202b',
          paid: stallsPermanent.paidAmount,
          sold: stallsPermanent.soldAmount,
          refunded: stallsPermanent.refundedAmount,
          scratched: stallsPermanent.scratchedAmount,
          banked: stallsPermanent.bankedAmount,
          pending: stallsPermanent.pendingAmount,
          forecast: stallsPermanent.forecastAmount,
          description: 'Stalls - Permanent',
          transactions: stallsPermanent.paidItems.length,
        },
        {
          account: 'P-202c',
          paid: stallsPrivate.paidAmount,
          sold: stallsPrivate.soldAmount,
          refunded: stallsPrivate.refundedAmount,
          scratched: stallsPrivate.scratchedAmount,
          banked: stallsPrivate.bankedAmount,
          pending: stallsPrivate.pendingAmount,
          forecast: stallsPrivate.forecastAmount,
          description: 'Stalls - Private',
          transactions: stallsPrivate.paidItems.length,
        },
        {
          account: 'P-202d',
          paid: stallsCircuit.paidAmount,
          sold: stallsCircuit.soldAmount,
          refunded: stallsCircuit.refundedAmount,
          scratched: stallsCircuit.scratchedAmount,
          banked: stallsCircuit.bankedAmount,
          pending: stallsCircuit.pendingAmount,
          forecast: stallsCircuit.forecastAmount,
          description: 'Stalls - Circuit',
          transactions: stallsCircuit.paidItems.length,
        },
        {
          account: 'P-202e',
          paid: stallsMuckFee.paidAmount,
          sold: stallsMuckFee.soldAmount,
          refunded: stallsMuckFee.refundedAmount,
          scratched: stallsMuckFee.scratchedAmount,
          banked: stallsMuckFee.bankedAmount,
          pending: stallsMuckFee.pendingAmount,
          forecast: stallsMuckFee.forecastAmount,
          description: 'Stalls - Muck fee',
          transactions: stallsMuckFee.paidItems.length,
        },
        ...customRows(stallsCustom, 'Stalls', 'P-202', 102, eventPaymentSettings?.chargeSalesTax),
        // Bedding
        {
          account: 'P-203a',
          paid: beddingShavings.paidAmount,
          sold: beddingShavings.soldAmount,
          refunded: beddingShavings.refundedAmount,
          scratched: beddingShavings.scratchedAmount,
          banked: beddingShavings.bankedAmount,
          pending: beddingShavings.pendingAmount,
          forecast: beddingShavings.forecastAmount,
          description: 'Bedding - Shavings',
          transactions: beddingShavings.paidItems.length,
        },
        {
          account: 'P-203b',
          paid: beddingStraw.paidAmount,
          sold: beddingStraw.soldAmount,
          refunded: beddingStraw.refundedAmount,
          scratched: beddingStraw.scratchedAmount,
          banked: beddingStraw.bankedAmount,
          pending: beddingStraw.pendingAmount,
          forecast: beddingStraw.forecastAmount,
          description: 'Bedding - Straw',
          transactions: beddingStraw.paidItems.length,
        },
        ...customRows(beddingCustom, 'Bedding', 'P-203', 99, eventPaymentSettings?.chargeSalesTax),
        // Feed
        {
          account: 'P-204a',
          paid: feedCoastalHay.paidAmount,
          sold: feedCoastalHay.soldAmount,
          refunded: feedCoastalHay.refundedAmount,
          scratched: feedCoastalHay.scratchedAmount,
          banked: feedCoastalHay.bankedAmount,
          pending: feedCoastalHay.pendingAmount,
          forecast: feedCoastalHay.forecastAmount,
          description: 'Feed - Coastal Hay',
          transactions: feedCoastalHay.paidItems.length,
        },
        {
          account: 'P-204b',
          paid: feedTimothyHay.paidAmount,
          sold: feedTimothyHay.soldAmount,
          refunded: feedTimothyHay.refundedAmount,
          scratched: feedTimothyHay.scratchedAmount,
          banked: feedTimothyHay.bankedAmount,
          pending: feedTimothyHay.pendingAmount,
          forecast: feedTimothyHay.forecastAmount,
          description: 'Feed - Timothy Hay',
          transactions: feedTimothyHay.paidItems.length,
        },
        {
          account: 'P-204c',
          paid: feedAlfalfaHay.paidAmount,
          sold: feedAlfalfaHay.soldAmount,
          refunded: feedAlfalfaHay.refundedAmount,
          scratched: feedAlfalfaHay.scratchedAmount,
          banked: feedAlfalfaHay.bankedAmount,
          pending: feedAlfalfaHay.pendingAmount,
          forecast: feedAlfalfaHay.forecastAmount,
          description: 'Feed - Alfalfa Hay',
          transactions: feedAlfalfaHay.paidItems.length,
        },
        {
          account: 'P-204d',
          paid: feedTnAHay.paidAmount,
          sold: feedTnAHay.soldAmount,
          refunded: feedTnAHay.refundedAmount,
          scratched: feedTnAHay.scratchedAmount,
          banked: feedTnAHay.bankedAmount,
          pending: feedTnAHay.pendingAmount,
          forecast: feedTnAHay.forecastAmount,
          description: 'Feed - TnA Hay',
          transactions: feedTnAHay.paidItems.length,
        },
        ...customRows(feedCustom, 'Feed', 'P-204', 101, eventPaymentSettings?.chargeSalesTax),
        // Other
        {
          account: 'P-205a',
          paid: otherPaddock.paidAmount,
          sold: otherPaddock.soldAmount,
          refunded: otherPaddock.refundedAmount,
          scratched: otherPaddock.scratchedAmount,
          banked: otherPaddock.bankedAmount,
          pending: otherPaddock.pendingAmount,
          forecast: otherPaddock.forecastAmount,
          description: 'Other - Paddock',
          transactions: otherPaddock.paidItems.length,
        },
        {
          account: 'P-205b',
          paid: otherRVHookup.paidAmount,
          sold: otherRVHookup.soldAmount,
          refunded: otherRVHookup.refundedAmount,
          scratched: otherRVHookup.scratchedAmount,
          banked: otherRVHookup.bankedAmount,
          pending: otherRVHookup.pendingAmount,
          forecast: otherRVHookup.forecastAmount,
          description: 'Other - RV Hookup',
          transactions: otherRVHookup.paidItems.length,
        },
        ...customRows(otherCustom, 'Other', 'P-205', 99, eventPaymentSettings?.chargeSalesTax),
        // Mandatory
        {
          account: 'P-206a',
          paid: mandatoryOfficeFee.paidAmount,
          sold: mandatoryOfficeFee.soldAmount,
          refunded: mandatoryOfficeFee.refundedAmount,
          scratched: mandatoryOfficeFee.scratchedAmount,
          banked: mandatoryOfficeFee.bankedAmount,
          pending: mandatoryOfficeFee.pendingAmount,
          forecast: mandatoryOfficeFee.forecastAmount,
          description: 'Mandatory - Office Fee',
          transactions: mandatoryOfficeFee.paidItems.length,
        },
        {
          account: 'P-206b',
          paid: mandatoryShipInGroundFee.paidAmount,
          sold: mandatoryShipInGroundFee.soldAmount,
          refunded: mandatoryShipInGroundFee.refundedAmount,
          scratched: mandatoryShipInGroundFee.scratchedAmount,
          banked: mandatoryShipInGroundFee.bankedAmount,
          pending: mandatoryShipInGroundFee.pendingAmount,
          forecast: mandatoryShipInGroundFee.forecastAmount,
          description: 'Mandatory - Ship in / Ground fee',
          transactions: mandatoryShipInGroundFee.paidItems.length,
        },
        {
          account: 'P-206c',
          paid: mandatoryAmbulanceFee.paidAmount,
          sold: mandatoryAmbulanceFee.soldAmount,
          refunded: mandatoryAmbulanceFee.refundedAmount,
          scratched: mandatoryAmbulanceFee.scratchedAmount,
          banked: mandatoryAmbulanceFee.bankedAmount,
          pending: mandatoryAmbulanceFee.pendingAmount,
          forecast: mandatoryAmbulanceFee.forecastAmount,
          description: 'Mandatory - Ambulance',
          transactions: mandatoryAmbulanceFee.paidItems.length,
        },
        {
          account: 'P-206d',
          paid: mandatoryNightWatch.paidAmount,
          sold: mandatoryNightWatch.soldAmount,
          refunded: mandatoryNightWatch.refundedAmount,
          scratched: mandatoryNightWatch.scratchedAmount,
          banked: mandatoryNightWatch.bankedAmount,
          pending: mandatoryNightWatch.pendingAmount,
          forecast: mandatoryNightWatch.forecastAmount,
          description: 'Mandatory - Night Watch',
          transactions: mandatoryNightWatch.paidItems.length,
        },
        ...customRows(
          mandatoryCustom,
          'Mandatory',
          'P-206',
          101,
          eventPaymentSettings?.chargeSalesTax
        ), // e
        // Tickets
        {
          account: 'P-207',
          paid: tickets.paidAmount,
          sold: tickets.soldAmount,
          refunded: tickets.refundedAmount,
          scratched: tickets.scratchedAmount,
          banked: tickets.bankedAmount,
          pending: tickets.pendingAmount,
          forecast: tickets.forecastAmount,
          description: 'Tickets',
          transactions: tickets.paidItems.length,
        },
      ]

      const TRANSACTIONS_ROWS: TransactionRow[] = [
        ...registrationsByDay.map((item) => ({
          recipientName: item.recipientName || '',
          feeName: `${item.registrationByDayName} ${item.order ? `(${item.order})` : ''}`,
          amount: item.registrationPrice || 0,
          salesTax: addSalesTax(
            item.created,
            (item.registrationPrice || 0) + (item.isQualify ? Number(item.qualifyFee || 0) : 0),
            item.includeSalesTax,
            eventPaymentSettings?.chargeSalesTax
          ),
          isQualify: item.isQualify || null,
          qualifyFee: item.isQualify ? Number(item.qualifyFee || 0) : null,
          paymentType: item.paymentType || null,
          paymentStatus: item.isScratched ? 'scratched' : item.paymentStatus || 'pending',
          amountRefunded: item.refundAmountAdded ? item.amountRefunded || 0 : null,
          amountRefundedSalesTax: addSalesTax(
            item.created,
            item.amountRefunded || 0,
            item.includeSalesTax,
            eventPaymentSettings?.chargeSalesTax
          ),
          created: item.created ? moment(item?.created).format('MMM DD, YYYY h:mm:ss a') : '',
          paidDate: item.paidDate ? moment(item?.paidDate).format('MMM DD, YYYY h:mm:ss a') : '',
        })),
        ...registrationsFees.map((item) => ({
          recipientName: item.recipientName || '',
          feeName: item.feesTitle || '',
          amount: item.feesPrice,
          salesTax: addSalesTax(
            item.created,
            item.feesPrice || 0,
            item.includeSalesTax,
            eventPaymentSettings?.chargeSalesTax
          ),
          paymentType: item.paymentType || null,
          paymentStatus: item.isScratched ? 'scratched' : item.paymentStatus || 'pending',
          amountRefunded: item.refundAmountAdded
            ? (item.amountRefunded || 0) +
              addSalesTax(
                item.created,
                item.amountRefunded || 0,
                item.includeSalesTax,
                eventPaymentSettings?.chargeSalesTax
              )
            : null,
          created: item.created ? moment(item?.created).format('MMM DD, YYYY h:mm:ss a') : '',
          paidDate: item.paidDate ? moment(item?.paidDate).format('MMM DD, YYYY h:mm:ss a') : '',
        })),
        ...registrationsTickets.map((item) => ({
          recipientName: item.recipientName || '',
          feeName: item.ticketTitle || '',
          amount: item.ticketPrice,
          salesTax: addSalesTax(
            item.created,
            item.ticketPrice || 0,
            item.includeSalesTax,
            eventPaymentSettings?.chargeSalesTax
          ),
          paymentType: item.paymentType || null,
          paymentStatus: item.isScratched ? 'scratched' : item.paymentStatus || 'pending',
          amountRefunded: item.refundAmountAdded
            ? (item.amountRefunded || 0) +
              addSalesTax(
                item.created,
                item.amountRefunded || 0,
                item.includeSalesTax,
                eventPaymentSettings?.chargeSalesTax
              )
            : null,
          created: item.created ? moment(item?.created).format('MMM DD, YYYY h:mm:ss a') : '',
          paidDate: item.paidDate ? moment(item?.paidDate).format('MMM DD, YYYY h:mm:ss a') : '',
        })),
      ]

      const workbook = XLSX.utils.book_new()
      const showField = `${event?.eventName} - ${moment(event?.eventStartDate).format('D MMM, YYYY')}`

      generatePaymentMethodsFinanceReport({
        workbook: workbook,
        rows: PAYMENT_METHOD_ROWS,
        showField: showField,
        currency: getCurrency(event?.currency),
      })

      generateCategoriesFinanceReport({
        workbook: workbook,
        rows: CATEGORIES_ROWS,
        showField: showField,
        currency: getCurrency(event?.currency),
      })

      generateTransactionsFinanceReport({
        workbook: workbook,
        rows: TRANSACTIONS_ROWS,
        currency: getCurrency(event?.currency),
      })

      // ── Write a file (triggers download in browser) ─────────────────────────────
      XLSX.writeFile(workbook, `${event?.eventName}_finance_export.xlsx`)

      toast.success({
        message: MESSAGES_CONST.REPORT_SUCCESSFULLY_DOWNLOADED,
      })
    } catch (error) {
      toast.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
      console.error('error: ', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      disabled={loading}
      className="group border border-solid border-SeabiscuitDark200ThemeColor min-w-[120px] flex gap-2 justify-center items-center text-SeabiscuitDark200ThemeColor rounded-full p-2.5 bg-transparent hover:text-white hover:bg-SeabiscuitMainThemeColor hover:border-SeabiscuitMainThemeColor px-5 text-sm"
      onClick={openExport}
    >
      Export
      {loading ? (
        <Loader2Icon className="animate-spin w-4 h-4" />
      ) : (
        <>
          <img
            src="/assets/og_icons/DownloadfromtheCloud-1.svg"
            className="block group-hover:hidden w-5"
            alt=""
          />
          <img
            src="/assets/og_icons/DownloadfromtheCloud-2.svg"
            className="hidden group-hover:block w-5"
            alt=""
          />
        </>
      )}
    </button>
  )
}
